import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Leaf, Users2, FileCheck, Globe, Users,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import SocialResponsibility from './finance/SocialResponsibility';
import { mockData } from '../data/mockData';

export const ESGPage = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSectionState] = useState(searchParams.get('section') || 'overview');
  const [year, setYear] = useState(2025);

  // Wrapper function that scrolls to top when changing section
  const setActiveSection = (newSection) => {
    setActiveSectionState(newSection);
    // Scroll to top when changing section
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  };

  // Update URL when section changes
  useEffect(() => {
    if (activeSection && activeSection !== 'overview') {
      setSearchParams({ section: activeSection });
    } else {
      setSearchParams({});
    }
  }, [activeSection, setSearchParams]);

  // Get yearly data for SocialResponsibility component
  const yearlyData = mockData.finance.yearly[year];
  const prevYearData = mockData.finance.yearly[year - 1] || mockData.finance.yearly[2024];

  const menuItems = [
    { id: 'overview', label: { en: 'ESG Overview', pt: 'Visão Geral ESG' }, icon: Globe },
    { id: 'social-responsibility', label: { en: 'Social Responsibility', pt: 'Responsabilidade Social' }, icon: Users },
    { id: 'safety', label: { en: 'Safety', pt: 'Segurança' }, icon: Shield },
    { id: 'environment', label: { en: 'Environment', pt: 'Ambiente' }, icon: Leaf },
    { id: 'communities', label: { en: 'Communities', pt: 'Comunidades' }, icon: Users2 },
    { id: 'compliance', label: { en: 'Compliance', pt: 'Compliance' }, icon: FileCheck },
  ];

  // Helper function to get the current section title
  const getSectionTitle = () => {
    const section = menuItems.find(item => item.id === activeSection);
    return section ? section.label[language] : (language === 'pt' ? 'Visão Geral' : 'Overview');
  };

  const placeholderKpis = [
    { label: { en: 'Safety', pt: 'Segurança' }, icon: Shield },
    { label: { en: 'Environment', pt: 'Ambiente' }, icon: Leaf },
    { label: { en: 'Communities', pt: 'Comunidades' }, icon: Users2 },
    { label: { en: 'Compliance', pt: 'Compliance' }, icon: FileCheck },
  ];

  const renderPlaceholderContent = (title) => (
    <div className="flex flex-col items-center justify-center min-h-[500px]">
      <div className="relative mb-10">
        <div className="w-32 h-32 bg-slate-100 rounded-full flex items-center justify-center">
          <Leaf className="w-16 h-16 text-slate-300" />
        </div>
        <div className="absolute -inset-4 border-2 border-dashed border-slate-200 rounded-full animate-pulse"></div>
      </div>

      <h2 className="font-serif text-3xl font-semibold text-primary mb-4 text-center">
        {title || t('moduleInDevelopment')}
      </h2>
      <p className="text-slate-500 max-w-xl text-center leading-relaxed mb-10">
        {t('placeholderText')}
      </p>

      <div className="grid grid-cols-4 gap-4 w-full max-w-3xl mb-10">
        {placeholderKpis.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <div 
              key={idx} 
              className="bg-white/60 border border-dashed border-slate-200 rounded-lg p-6 text-center opacity-50"
            >
              <div className="w-12 h-12 bg-slate-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                <Icon className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">
                {typeof kpi.label === 'object' ? kpi.label[language] : kpi.label}
              </p>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-400 uppercase tracking-wider">
        {t('activationNote')}
      </p>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'social-responsibility':
        return (
          <SocialResponsibility
            year={year}
            language={language}
            yearlyData={yearlyData}
            prevYearData={prevYearData}
            setActiveSection={setActiveSection}
          />
        );
      case 'safety':
        return renderPlaceholderContent(language === 'pt' ? 'Segurança' : 'Safety');
      case 'environment':
        return renderPlaceholderContent(language === 'pt' ? 'Ambiente' : 'Environment');
      case 'communities':
        return renderPlaceholderContent(language === 'pt' ? 'Comunidades' : 'Communities');
      case 'compliance':
        return renderPlaceholderContent(language === 'pt' ? 'Compliance' : 'Compliance');
      default:
        return renderPlaceholderContent();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="esg-page">
      {/* Sidebar - Primary color style like Finance */}
      <aside className="w-72 bg-primary h-screen sticky top-0 py-8 px-4 flex-shrink-0 overflow-y-auto">
        <nav className="space-y-1">
          <div className="px-3 py-2 mb-4">
            <h3 className="text-xs font-semibold text-white/60 uppercase tracking-wider">
              ESG
            </h3>
          </div>
          
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                  isActive 
                    ? 'bg-white/20 text-white font-semibold' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`esg-menu-${item.id}`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label[language]}</span>
              </button>
            );
          })}
        </nav>

        {/* Year Selector */}
        <div className="mt-8 px-4">
          <p className="text-xs text-white/60 uppercase tracking-wider mb-2">
            {language === 'pt' ? 'Ano' : 'Year'}
          </p>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-white/20 rounded-lg text-sm bg-white/10 text-white"
          >
            <option value={2024} className="text-primary">2024</option>
            <option value={2025} className="text-primary">2025</option>
            <option value={2026} className="text-primary">2026</option>
          </select>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header - Premium Style */}
        <div className="bg-white border-b border-slate-200 px-8 py-3 sticky top-0 z-40 shadow-sm">
          <div className="flex items-center justify-between">
            {/* Left side: Navigation + Department + Section */}
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/')}
                className="text-slate-600 hover:text-primary"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {language === 'pt' ? 'Cockpit Executivo' : 'Executive Cockpit'}
              </Button>
              <div className="h-4 w-px bg-slate-300"></div>
              <span className="font-serif font-bold text-primary text-lg">
                ESG
              </span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="font-serif text-slate-600 text-base">
                {getSectionTitle()}
              </span>
            </div>

            {/* Right side: Company name + Language Toggle */}
            <div className="flex items-center gap-4">
              <span className="font-serif text-slate-500 text-sm hidden lg:inline">
                {language === 'pt' ? 'Empresa ABC' : 'Company ABC'}
              </span>
              <div className="h-4 w-px bg-slate-200 hidden lg:block"></div>
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded hover:bg-primary/20 transition-colors text-primary"
                data-testid="esg-language-toggle"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium uppercase">{language}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-8 flex-1 bg-gradient-to-br from-slate-100 via-emerald-50/30 to-teal-50/20 overflow-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ESGPage;
